// Devices (plan §7, §10): enrol a kiosk or a staff phone with a one-time code, approve it by
// typing the fingerprint the device shows, revoke a lost one.
import { useState } from "react";
import { Badge, Banner, Button, Dialog, DialogClose, Select, Table, TextField } from "@vavr/ui";
import { api, dateTime } from "../shared/index.ts";
import { useConsole, useGet, useAction } from "./data.tsx";
import { ConfirmButton, LoadError, Loading, Toolbar } from "./common.tsx";
import { LastSeen } from "./Board.tsx";

interface Device { id: string; kind: string; name: string; status: string; enrolled_at: number | null; last_seen_at: number | null; app_version: string | null; battery: number | null; outbox_depth: number | null; has_key: number; created_at: number }

export function Devices() {
  const { base, site, can } = useConsole();
  const devices = useGet<{ devices: Device[] }>(`${base}/devices`);
  const act = useAction();
  if (devices.loading) return <Loading />;
  if (devices.error) return <LoadError error={devices.error} retry={devices.reload} />;
  const list = devices.data?.devices ?? [];
  const waiting = list.filter((d) => d.status === "pending" && d.has_key);
  return (
    <div className="pmu-stack">
      <Toolbar actions={<AddDevice />} />
      {act.error && <Banner tone="danger" title={act.error} />}
      {waiting.map((d) => <ApproveDevice key={d.id} device={d} />)}
      <Table
        caption="Devices" hideCaption rowKey={(d) => d.id} rows={list} empty="No devices yet. Add the kiosk at your door first."
        columns={[
          { key: "name", header: "Device", rowHeader: true, render: (d) => <span className="pmu-stack pmu-c-tight"><span>{d.name}</span><span className="pmu-small pmu-muted">{d.kind === "kiosk" ? "Kiosk" : "Staff phone"}{d.app_version ? ` · ${d.app_version}` : ""}</span></span> },
          { key: "status", header: "Status", render: (d) => <DeviceStatus d={d} /> },
          { key: "seen", header: "Last seen", render: (d) => (d.status === "active" ? <LastSeen ts={d.last_seen_at} tz={site.tz} /> : "") },
          { key: "out", header: "Waiting to send", align: "end", render: (d) => (d.status !== "active" ? "" : d.outbox_depth ? <Badge tone="warning">{d.outbox_depth} records</Badge> : "None") },
          { key: "bat", header: "Battery", align: "end", render: (d) => (d.battery == null ? "" : `${d.battery}%`) },
          { key: "since", header: "Added", render: (d) => dateTime(d.created_at, site.tz) },
          {
            key: "act", header: <span className="pmu-sr-only">Actions</span>, align: "end", render: (d) => d.status === "revoked" || !can("device.revoke") ? null : (
              <ConfirmButton size="sm" variant="ghost" danger title={`Revoke ${d.name}?`} description="The device stops working at once. Anything it sends later is refused and logged. This cannot be undone; enrol it again to reuse it." confirmLabel="Revoke"
                onConfirm={() => act.run(() => api("POST", `${base}/devices/${d.id}/revoke`), { success: `${d.name} revoked.`, invalidate: [`${base}/devices`, `${base}/board`] })}>Revoke</ConfirmButton>
            ),
          },
        ]}
      />
    </div>
  );
}

function DeviceStatus({ d }: { d: Device }) {
  if (d.status === "active") return <Badge tone="success">Active</Badge>;
  if (d.status === "revoked") return <Badge>Revoked</Badge>;
  return d.has_key ? <Badge tone="warning">Waiting for approval</Badge> : <Badge tone="info">Waiting for the code</Badge>;
}

function AddDevice() {
  const { base, site } = useConsole();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ kind: "kiosk", name: "" });
  const [code, setCode] = useState<{ enrol_code: string; expires_at: number } | null>(null);
  const act = useAction();
  const create = async () => {
    const r = await act.run(() => api("POST", `${base}/devices`, f), { invalidate: [`${base}/devices`] });
    if (r) setCode(r);
  };
  return (
    <Dialog title={code ? "Type this code on the device" : "Add a kiosk or phone"} open={open} onOpenChange={(o) => { setOpen(o); if (o) { setCode(null); setF({ kind: "kiosk", name: "" }); } }}
      trigger={<Button icon="plus">Add a kiosk or phone</Button>}
      actions={code ? <DialogClose><Button>Done</Button></DialogClose> : <><DialogClose><Button variant="secondary">Cancel</Button></DialogClose><Button loading={act.busy} disabled={!f.name.trim()} onClick={create}>Get a code</Button></>}>
      {code ? (
        <div className="pmu-stack">
          <p className="pmu-c-code">{code.enrol_code}</p>
          <p>Open Vavr on the {f.kind === "kiosk" ? "tablet" : "phone"} and enter this code. It works once and expires {dateTime(code.expires_at, site.tz)}.</p>
          <p className="pmu-muted">The device then shows a fingerprint. Come back here and type it to approve the device.</p>
        </div>
      ) : (
        <form className="pmu-stack" onSubmit={(e) => { e.preventDefault(); if (f.name.trim()) void create(); }}>
          {act.error && <Banner tone="danger" title={act.error} />}
          <Select label="Kind" value={f.kind} onValueChange={(v) => setF({ ...f, kind: v ?? "kiosk" })} options={[{ value: "kiosk", label: "Kiosk tablet at a door" }, { value: "phone", label: "Staff phone (roaming handovers)" }]} />
          <TextField label="Name" required placeholder="Front door, Playground phone" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <button type="submit" hidden />
        </form>
      )}
    </Dialog>
  );
}

function ApproveDevice({ device }: { device: Device }) {
  const { base } = useConsole();
  const [fp, setFp] = useState("");
  const act = useAction();
  const clean = fp.toUpperCase().replace(/[^0-9A-F]/g, "");
  const formatted = clean.match(/.{1,4}/g)?.join("-") ?? "";
  return (
    <Banner tone="warning" title={`${device.name} is waiting for approval`}>
      <form className="pmu-row pmu-c-approve" onSubmit={async (e) => { e.preventDefault(); await act.run(() => api("POST", `${base}/devices/${device.id}/approve`, { fingerprint: formatted }), { success: `${device.name} approved.`, invalidate: [`${base}/devices`, `${base}/board`] }); }}>
        <TextField label="Fingerprint shown on the device" placeholder="XXXX-XXXX-XXXX-XXXX" value={fp} onChange={(e) => setFp(e.target.value)} autoComplete="off" spellCheck={false} className="pmu-c-fp" />
        <Button type="submit" loading={act.busy} disabled={clean.length !== 16}>Approve</Button>
      </form>
      {act.error ? <p className="pmu-c-error" role="alert">{act.error}</p> : <p className="pmu-small">Only approve if it matches exactly. A different fingerprint means a different key, possibly someone else's.</p>}
    </Banner>
  );
}
